import { z } from 'zod';
import {
  ANCHORS,
  ARROW_HEADS,
  BOARD_FONT_KEYS,
  EMBED_PROVIDERS,
  LIMITS,
  LINE_TYPES,
  SHAPE_KINDS,
  STICKY_SIZES,
  STROKE_STYLES,
  TEXT_ALIGNS,
  VERTICAL_ALIGNS,
  type BoardFont,
  type ObjectType,
} from './constants.js';
import { STICKY_COLOR_KEYS } from './colors.js';
import { richDocSchema, type RichDoc } from './richtext.js';

const finite = z.number().finite();
const coord = finite.min(-10_000_000).max(10_000_000);
const size = finite.min(0).max(1_000_000);
const color = z.string().max(64);
const font = z.enum(BOARD_FONT_KEYS as [BoardFont, ...BoardFont[]]);
const fontSize = finite.min(4).max(1000);

/** Базовые поля любого объекта доски. */
export const baseObjectSchema = z.object({
  id: z.string().min(1).max(64),
  x: coord,
  y: coord,
  w: size,
  h: size,
  rotation: finite.default(0),
  /** Fractional index для z-порядка. */
  index: z.string().min(1).max(512),
  /** Фрейм или группа, в которую вложен объект. */
  parentId: z.string().max(64).nullable().default(null),
  locked: z.boolean().default(false),
  createdBy: z.string().max(64).nullable().default(null),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  /** Rich text (для типов с текстом). В Yjs хранится как Y.XmlFragment. */
  text: richDocSchema.optional(),
});

const textStyle = {
  fontFamily: font.default('inter'),
  fontSize: z.union([fontSize, z.literal('auto')]).default('auto'),
  align: z.enum(TEXT_ALIGNS).default('center'),
  valign: z.enum(VERTICAL_ALIGNS).default('middle'),
};

export const stickyPropsSchema = z.object({
  color: z.enum(STICKY_COLOR_KEYS).default('yellow'),
  size: z.enum(Object.keys(STICKY_SIZES) as ['S', 'M', 'L']).default('M'),
  ...textStyle,
  showAuthor: z.boolean().default(false),
  authorName: z.string().max(120).nullable().default(null),
});

export const shapePropsSchema = z.object({
  kind: z.enum(SHAPE_KINDS).default('rect'),
  fill: color.default('fill:white'),
  fillOpacity: finite.min(0).max(1).default(1),
  stroke: color.default('ink:ink'),
  strokeWidth: finite.min(0).max(64).default(2),
  strokeStyle: z.enum(STROKE_STYLES).default('solid'),
  textColor: color.default('ink:ink'),
  ...textStyle,
  fontSize: z.union([fontSize, z.literal('auto')]).default(18),
});

export const textPropsSchema = z.object({
  fontFamily: font.default('inter'),
  fontSize: fontSize.default(24),
  color: color.default('ink:ink'),
  align: z.enum(TEXT_ALIGNS).default('left'),
  /** true — ширина подстраивается под текст, false — фиксированная ширина с переносом. */
  autoWidth: z.boolean().default(true),
  background: color.nullable().default(null),
});

export const penPropsSchema = z.object({
  /** Плоский массив [x, y, pressure, x, y, pressure, ...] относительно (x, y) объекта. */
  points: z.array(finite).max(LIMITS.maxPenPoints * 3),
  color: color.default('ink:ink'),
  size: finite.min(0.5).max(200).default(4),
  highlighter: z.boolean().default(false),
  opacity: finite.min(0).max(1).default(1),
});

export const imagePropsSchema = z.object({
  fileId: z.string().max(64).nullable().default(null),
  /** Прямой URL (для внешних картинок, шаблонов). */
  src: z.string().max(4096).nullable().default(null),
  naturalWidth: size.default(0),
  naturalHeight: size.default(0),
  /** Обрезка в долях исходника: 0..1. */
  crop: z
    .object({ x: finite.min(0).max(1), y: finite.min(0).max(1), w: finite.min(0).max(1), h: finite.min(0).max(1) })
    .nullable()
    .default(null),
  alt: z.string().max(500).default(''),
});

export const connectorEndpointSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('object'), objectId: z.string().max(64), anchor: z.enum(ANCHORS).default('auto') }),
  z.object({ kind: z.literal('point'), x: coord, y: coord }),
]);
export type ConnectorEndpoint = z.infer<typeof connectorEndpointSchema>;

export const connectorPropsSchema = z.object({
  from: connectorEndpointSchema,
  to: connectorEndpointSchema,
  lineType: z.enum(LINE_TYPES).default('elbow'),
  startHead: z.enum(ARROW_HEADS).default('none'),
  endHead: z.enum(ARROW_HEADS).default('arrow'),
  stroke: color.default('ink:ink'),
  strokeWidth: finite.min(0.5).max(32).default(2),
  strokeStyle: z.enum(STROKE_STYLES).default('solid'),
  label: z.string().max(500).default(''),
  /** Положение подписи вдоль линии, 0..1. */
  labelPosition: finite.min(0).max(1).default(0.5),
  /** Обходить объекты при ортогональной разводке. */
  avoidObstacles: z.boolean().default(true),
});

export const framePropsSchema = z.object({
  title: z.string().max(200).default('Фрейм'),
  background: color.default('fill:white'),
  /** Порядок показа в презентации; меньше — раньше. */
  presentationOrder: finite.default(0),
  clipContent: z.boolean().default(true),
});

export const cardPropsSchema = z.object({
  title: z.string().max(500).default(''),
  status: z.string().max(64).default('todo'),
  assigneeId: z.string().max(64).nullable().default(null),
  assigneeName: z.string().max(120).nullable().default(null),
  dueDate: z.string().max(32).nullable().default(null),
  tags: z.array(z.string().max(40)).max(20).default([]),
  accent: color.default('ink:violet'),
  /** Чек-лист: пункты отмечаются в окне карточки или кликом по квадратику на холсте. */
  checklist: z
    .array(z.object({ id: z.string().max(32), text: z.string().max(300), done: z.boolean().default(false) }))
    .max(50)
    .default([]),
});

export const tablePropsSchema = z.object({
  rows: z.array(z.object({ id: z.string().max(32), h: size })).max(200),
  cols: z.array(z.object({ id: z.string().max(32), w: size })).max(100),
  /** Ключ — `${rowId}:${colId}`. */
  cells: z.record(z.string(), z.string().max(5_000)).default({}),
  headerRow: z.boolean().default(true),
  stroke: color.default('ink:silver'),
  headerFill: color.default('fill:lavender'),
  fontFamily: font.default('inter'),
  fontSize: fontSize.default(14),
});

export const mindmapPropsSchema = z.object({
  /** null — корень карты. */
  parentNodeId: z.string().max(64).nullable().default(null),
  rootId: z.string().max(64).nullable().default(null),
  direction: z.enum(['right', 'left']).default('right'),
  /** Порядок среди соседей (fractional index). */
  order: z.string().max(512).default('a0'),
  collapsed: z.boolean().default(false),
  branchColor: color.default('ink:violet'),
  style: z.enum(['pill', 'rect', 'underline']).default('pill'),
  fontFamily: font.default('inter'),
  fontSize: fontSize.default(16),
});

export const embedPropsSchema = z.object({
  url: z.string().max(4096),
  provider: z.enum(EMBED_PROVIDERS).default('link'),
  embedUrl: z.string().max(4096).nullable().default(null),
  title: z.string().max(500).default(''),
  description: z.string().max(2000).default(''),
  imageUrl: z.string().max(4096).nullable().default(null),
  siteName: z.string().max(200).default(''),
});

export const stampPropsSchema = z.object({
  emoji: z.string().min(1).max(32),
  kind: z.enum(['stamp', 'reaction']).default('stamp'),
  authorName: z.string().max(120).nullable().default(null),
});

export const voteDotPropsSchema = z.object({
  sessionId: z.string().max(64),
  targetId: z.string().max(64),
  userId: z.string().max(64),
  color: color.default('ink:violet'),
});

export const groupPropsSchema = z.object({});

export const objectPropsSchemas = {
  sticky: stickyPropsSchema,
  shape: shapePropsSchema,
  text: textPropsSchema,
  pen: penPropsSchema,
  image: imagePropsSchema,
  connector: connectorPropsSchema,
  frame: framePropsSchema,
  card: cardPropsSchema,
  table: tablePropsSchema,
  'mindmap-node': mindmapPropsSchema,
  embed: embedPropsSchema,
  stamp: stampPropsSchema,
  'vote-dot': voteDotPropsSchema,
  group: groupPropsSchema,
} as const satisfies Record<ObjectType, z.ZodTypeAny>;

function variant<T extends ObjectType, P extends z.ZodTypeAny>(type: T, props: P) {
  return baseObjectSchema.extend({ type: z.literal(type), props });
}

export const boardObjectSchema = z.discriminatedUnion('type', [
  variant('sticky', stickyPropsSchema),
  variant('shape', shapePropsSchema),
  variant('text', textPropsSchema),
  variant('pen', penPropsSchema),
  variant('image', imagePropsSchema),
  variant('connector', connectorPropsSchema),
  variant('frame', framePropsSchema),
  variant('card', cardPropsSchema),
  variant('table', tablePropsSchema),
  variant('mindmap-node', mindmapPropsSchema),
  variant('embed', embedPropsSchema),
  variant('stamp', stampPropsSchema),
  variant('vote-dot', voteDotPropsSchema),
  variant('group', groupPropsSchema),
]);

export type BoardObject = z.infer<typeof boardObjectSchema>;
export type BoardObjectInput = z.input<typeof boardObjectSchema>;
export type ObjectOfType<T extends ObjectType> = Extract<BoardObject, { type: T }>;
export type PropsOf<T extends ObjectType> = z.infer<(typeof objectPropsSchemas)[T]>;

export type StickyObject = ObjectOfType<'sticky'>;
export type ShapeObject = ObjectOfType<'shape'>;
export type TextObject = ObjectOfType<'text'>;
export type PenObject = ObjectOfType<'pen'>;
export type ImageObject = ObjectOfType<'image'>;
export type ConnectorObject = ObjectOfType<'connector'>;
export type FrameObject = ObjectOfType<'frame'>;
export type CardObject = ObjectOfType<'card'>;
export type TableObject = ObjectOfType<'table'>;
export type MindmapNodeObject = ObjectOfType<'mindmap-node'>;
export type EmbedObject = ObjectOfType<'embed'>;
export type StampObject = ObjectOfType<'stamp'>;
export type VoteDotObject = ObjectOfType<'vote-dot'>;
export type GroupObject = ObjectOfType<'group'>;

/** Размер по умолчанию при создании кликом. */
export const DEFAULT_SIZES: Record<ObjectType, { w: number; h: number }> = {
  sticky: { w: STICKY_SIZES.M, h: STICKY_SIZES.M },
  shape: { w: 200, h: 140 },
  text: { w: 240, h: 36 },
  pen: { w: 1, h: 1 },
  image: { w: 320, h: 240 },
  connector: { w: 1, h: 1 },
  frame: { w: 800, h: 600 },
  card: { w: 280, h: 160 },
  table: { w: 480, h: 160 },
  'mindmap-node': { w: 180, h: 48 },
  embed: { w: 480, h: 270 },
  stamp: { w: 64, h: 64 },
  'vote-dot': { w: 28, h: 28 },
  group: { w: 1, h: 1 },
};

/** Заполняет пропсы значениями по умолчанию и валидирует их. */
export function parseProps<T extends ObjectType>(type: T, props: unknown): PropsOf<T> {
  return objectPropsSchemas[type].parse(props ?? {}) as PropsOf<T>;
}

export function safeParseObject(input: unknown) {
  return boardObjectSchema.safeParse(input);
}

/** Схема для создания объекта через API/импорт: id, index, даты опциональны. */
export const createObjectInputSchema = z.object({
  id: z.string().min(1).max(64).optional(),
  type: z.enum(Object.keys(objectPropsSchemas) as [ObjectType, ...ObjectType[]]),
  x: coord.default(0),
  y: coord.default(0),
  w: size.optional(),
  h: size.optional(),
  rotation: finite.default(0),
  index: z.string().min(1).max(512).optional(),
  parentId: z.string().max(64).nullable().default(null),
  locked: z.boolean().default(false),
  props: z.record(z.string(), z.unknown()).default({}),
  /** Rich text документ или простая строка. */
  text: z.union([richDocSchema, z.string().max(LIMITS.maxTextLength)]).optional(),
});
export type CreateObjectInput = z.input<typeof createObjectInputSchema>;

export const updateObjectInputSchema = z.object({
  x: coord.optional(),
  y: coord.optional(),
  w: size.optional(),
  h: size.optional(),
  rotation: finite.optional(),
  index: z.string().min(1).max(512).optional(),
  parentId: z.string().max(64).nullable().optional(),
  locked: z.boolean().optional(),
  props: z.record(z.string(), z.unknown()).optional(),
  text: z.union([richDocSchema, z.string().max(LIMITS.maxTextLength)]).optional(),
});
export type UpdateObjectInput = z.input<typeof updateObjectInputSchema>;

export type { RichDoc };
